import React, { useState, useEffect } from 'react';
import { Stack, Button, FormControl } from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import dayjs, { Dayjs } from 'dayjs';
import { translate as t } from 'hooks/useTranslations';
import { PageToolbar } from '_lex/_atoms/PageToolbar';
import QuickSearch from 'components/_atoms/QuickSearch';
import debounce from 'lodash/debounce';
import ReactSelect, { components } from 'react-select';
import { compliancePerformanceColors } from '.';

interface TrendOption {
  value: string;
  label: string;
}

interface ComplianceOption {
  value: string;
  label: string;
}

interface FiltersProps {
  initialSearch: string;
  initialDateRange: DateRange<Dayjs> | null;
  initialTrend: string;
  initialCompliance: string;
  initialPolicyMods: string;
  updateParams: (params: any) => void;
  isFiltered: boolean;
  onClearFilters: () => void;
}

const Filters: React.FC<FiltersProps> = ({
  initialSearch,
  initialDateRange,
  initialTrend,
  initialCompliance,
  initialPolicyMods,
  updateParams,
  isFiltered,
  onClearFilters,
}) => {
  const [search, setSearch] = useState(initialSearch);
  const [dateRange, setDateRange] = useState<DateRange<Dayjs> | null>(initialDateRange);
  const [trend, setTrend] = useState(initialTrend);
  const [compliance, setCompliance] = useState(initialCompliance);
  const [policyMods, setPolicyMods] = useState(initialPolicyMods);

  useEffect(() => {
    setSearch(initialSearch);
    setDateRange(initialDateRange);
    setTrend(initialTrend);
    setCompliance(initialCompliance);
    setPolicyMods(initialPolicyMods);
  }, [initialSearch, initialDateRange, initialTrend, initialCompliance, initialPolicyMods]);

  const debouncedSearchChange = React.useMemo(
    () => debounce((value: string) => updateParams({ search: value }), 300),
    [updateParams]
  );

  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    debouncedSearchChange(value);
  };

  const handleDateRangeChange = (newValue: DateRange<Dayjs> | null) => {
    setDateRange(newValue);
    updateParams({
      startDate: newValue?.[0]?.format('YYYY-MM-DD') ?? '',
      endDate: newValue?.[1]?.format('YYYY-MM-DD') ?? '',
    });
  };

  const handleTrendChange = (newValue: any) => {
    const value = newValue?.value ?? '';
    setTrend(value);
    updateParams({ trend: value });
  };

  const handleComplianceChange = (newValue: any) => {
    const value = newValue?.value ?? '';
    setCompliance(value);
    updateParams({ compliance: value });
  };

  const handlePolicyModsChange = (newValue: any) => {
    const value = newValue?.value ?? '';
    setPolicyMods(value);
    updateParams({ policyMods: value });
  };

  const SingleValue = ({ children, ...props }: any) => (
    <components.SingleValue {...props}>Trend: {children}</components.SingleValue>
  );

  const ComplianceSingleValue = ({ children, ...props }: any) => (
    <components.SingleValue {...props}>Compliance: {children}</components.SingleValue>
  );

  const PolicyModSingleValue = ({ children, ...props }: any) => (
    <components.SingleValue {...props}>Policy Changes: {children}</components.SingleValue>
  );

  const trendOptions: TrendOption[] = [
    { value: '', label: 'All' },
    { value: 'UP', label: t('dashboard.riskPool.trend.UP') },
    { value: 'RISING', label: t('dashboard.riskPool.trend.RISING') },
    { value: 'NEUTRAL', label: t('dashboard.riskPool.trend.NEUTRAL') },
    { value: 'DECLINING', label: t('dashboard.riskPool.trend.DECLINING') },
    { value: 'DOWN', label: t('dashboard.riskPool.trend.DOWN') },
  ];

  const complianceOptions: ComplianceOption[] = [
    { value: '', label: 'All' },
    ...Object.keys(compliancePerformanceColors).map((performance) => ({
      value: performance,
      label: t(`dashboard.riskPool.complianceOverview.${performance}`),
    })),
  ];

  const policyModOptions = [
    { value: '', label: 'All' },
    { value: 'YES', label: t('dashboard.riskPool.agencyModificationsOverview.YES') },
    { value: 'NO', label: t('dashboard.riskPool.agencyModificationsOverview.NO') },
  ];

  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
      label: t('dashboard.riskPool.dateRangeFilters.shortcuts.last30Days'),
      getValue: () => {
        const today = dayjs();
        return [today.subtract(30, 'day'), today];
      },
    },
    {
      label: t('dashboard.riskPool.dateRangeFilters.shortcuts.lastMonth'),
      getValue: () => {
        const today = dayjs();
        const startOfLastMonth = today.startOf('month').subtract(1, 'month');
        return [startOfLastMonth, startOfLastMonth.endOf('month')];
      },
    },
    {
      label: t('dashboard.riskPool.dateRangeFilters.shortcuts.lastQuarter'),
      getValue: () => {
        const today = dayjs();
        const startOfLastQuarter = today.startOf('quarter').subtract(1, 'quarter');
        return [startOfLastQuarter, startOfLastQuarter.endOf('quarter')];
      },
    },
    {
      label: t('dashboard.riskPool.dateRangeFilters.shortcuts.thisYear'),
      getValue: () => {
        const today = dayjs();
        const startOfLastMonth = today.startOf('year');
        return [startOfLastMonth, today];
      },
    },
    {
      label: t('dashboard.riskPool.dateRangeFilters.shortcuts.lastYear'),
      getValue: () => {
        const today = dayjs();
        const startOfLastYear = today.startOf('year').subtract(1, 'year');
        return [startOfLastYear, startOfLastYear.endOf('year')];
      },
    },
  ];

  return (
    <PageToolbar
      leftItems={
        <Stack
          direction='row'
          gap={2}
        >
          <QuickSearch
            id='risk-pool-search'
            initialValue={search}
            onChange={handleSearchChange}
            placeholder='Search'
          />
          <DateRangePicker
            value={dateRange ?? undefined}
            onChange={handleDateRangeChange}
            localeText={{
              start: t('dashboard.riskPool.dateRangeFilters.start'),
              end: t('dashboard.riskPool.dateRangeFilters.end'),
            }}
            slotProps={{
              shortcuts: {
                items: shortcutsItems,
              },
              textField: {
                InputProps: {
                  size: 'small',
                },
              },
              actionBar: { actions: [] },
            }}
            sx={{
              '& .MuiFormControl-root': {
                paddingTop: 0,
              },
              '& .MuiFormLabel-root': {
                display: 'none',
              },
            }}
          />
          <FormControl sx={{ minWidth: 150, pt: 0 }}>
            <ReactSelect
              value={trendOptions.find((option) => option.value === trend)}
              options={trendOptions}
              placeholder='Trend:'
              menuPortalTarget={document.body}
              components={{ SingleValue }}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, width: 300 }),
                input: (base) => ({
                  ...base,
                  boxShadow: 'none',
                  outline: 0,
                  minHeight: 26,
                }),
              }}
              menuPosition='fixed'
              menuPlacement='auto'
              isSearchable={false}
              onChange={handleTrendChange}
            />
          </FormControl>
          <FormControl sx={{ minWidth: 150, pt: 0 }}>
            <ReactSelect
              value={complianceOptions.find((option) => option.value === compliance)}
              options={complianceOptions}
              placeholder='Compliance:'
              menuPortalTarget={document.body}
              components={{ SingleValue: ComplianceSingleValue }}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, width: 300 }),
                input: (base) => ({
                  ...base,
                  boxShadow: 'none',
                  outline: 0,
                  minHeight: 26,
                }),
              }}
              menuPosition='fixed'
              menuPlacement='auto'
              isSearchable={false}
              onChange={handleComplianceChange}
            />
          </FormControl>
          <FormControl sx={{ minWidth: 150, pt: 0 }}>
            <ReactSelect
              value={policyModOptions.find((option) => option.value === policyMods)}
              options={policyModOptions}
              placeholder='Policy Changes:'
              menuPortalTarget={document.body}
              components={{ SingleValue: PolicyModSingleValue }}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, width: 300 }),
                input: (base) => ({
                  ...base,
                  boxShadow: 'none',
                  outline: 0,
                  minHeight: 26,
                }),
              }}
              menuPosition='fixed'
              menuPlacement='auto'
              isSearchable={false}
              onChange={handlePolicyModsChange}
            />
          </FormControl>
          {isFiltered && (
            <Button
              color='neutral'
              onClick={onClearFilters}
              size='small'
            >
              {t('dashboard.riskPool.actions.clearFilters')}
            </Button>
          )}
        </Stack>
      }
    />
  );
};

export default Filters;
